import { FC } from 'react';
import { Alert, Box, Button, Chip, Paper, Tooltip, Typography } from '@mui/material';
import {
  EuroTwoTone,
  GroupsTwoTone,
  LoopTwoTone,
  PersonAddTwoTone,
  ScheduleTwoTone,
  ShoppingCartTwoTone,
  TrendingUpTwoTone,
  VideocamTwoTone,
  WarningAmberTwoTone,
} from '@mui/icons-material';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { tokens } from '../../theme';
import type {
  WebinarRevenueAttributionData,
  WebinarRevenuePerformanceData,
} from '../../helpers/webinarStatisticsData';
import { formatGermanTime } from '../../helpers/germanTime';
import { KPI, KPI_CONCEPTS, KpiKey, formatKpi } from '../../helpers/webinarKpiDefinitions';
import Explainer from '../../components/Explainer';
import KpiTile from '../../components/auswertungen/KpiTile';
import MetricsTable, { ExtraColumn } from '../../components/auswertungen/MetricsTable';

interface Props {
  data: WebinarRevenueAttributionData;
  colors: ReturnType<typeof tokens>;
  onOpenWebinar: (webinarId: string) => void;
}

const TYPE_CHIP: Record<string, { label: string; color: string; icon: JSX.Element }> = {
  evergreen: { label: 'Evergreen', color: '#ed6c02', icon: <LoopTwoTone /> },
  live: { label: 'Live', color: '#3da58a', icon: <VideocamTwoTone /> },
  hybrid: { label: 'Hybrid', color: '#ed6c02', icon: <VideocamTwoTone /> },
  replay: { label: 'Replay', color: '#0288d1', icon: <ScheduleTwoTone /> },
  automated: { label: 'Automated', color: '#535ac8', icon: <ScheduleTwoTone /> },
};

const typeChip = (row: { is_evergreen?: number; type?: string }) => {
  const t =
    row.is_evergreen === 1
      ? TYPE_CHIP.evergreen
      : (TYPE_CHIP[row.type ?? ''] ?? TYPE_CHIP.automated);
  return (
    <Chip
      icon={t.icon}
      label={t.label}
      size="small"
      sx={{
        backgroundColor: t.color,
        color: 'white',
        fontWeight: 'bold',
        '& .MuiChip-icon': { color: 'white' },
      }}
    />
  );
};

const TABLE_METRICS: KpiKey[] = [
  'registrations',
  'timely',
  'attendees',
  'show_up_rate',
  'orders',
  'registration_conversion_rate',
  'attendee_conversion_rate',
  'cpa_revenue',
  'cpa_per_registration',
  'cpa_per_attendee',
  'ltv_revenue',
  'ltv_per_attendee',
  'paid_revenue',
  'upsell_orders',
  'upsell_rate',
  'active_subscribers',
  'active_subscriber_rate',
  'unique_customers',
];

const TRACKING_HINT =
  'Tracking-Warnung: StealthSeminar hat für dieses Webinar kaum Teilnahme-Signale geliefert. Show-up und Teilnehmer-Conversion sind hier nicht belastbar; seine Teilnahme-Zahlen fließen nicht in Summenzeile und Kacheln ein.';

/** Tab „Umsatz-Attribution“ der Webinar-Auswertungen. */
const RevenueAttributionView: FC<Props> = ({ data, colors, onOpenWebinar }) => {
  const s = data.summary;
  const n = (v: number) => v.toLocaleString('de-DE');
  const excluded = data.performance.filter((r) => r.tracking_suspect);

  const leading: ExtraColumn<WebinarRevenuePerformanceData>[] = [
    {
      id: 'webinar_title',
      label: 'Webinar',
      sortValue: (r) => r.webinar_title,
      render: (r) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 180 }}>
          {r.webinar_title}
          {r.tracking_suspect && (
            <Tooltip title={TRACKING_HINT} arrow>
              <WarningAmberTwoTone sx={{ color: '#ed6c02', fontSize: 18, cursor: 'help' }} />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      id: 'product_name',
      label: 'Produkt',
      tooltip: 'Produkte der zugeordneten Anfos.',
      sortValue: (r) => r.product_name ?? '',
      render: (r) => r.product_name || r.product_code || '–',
    },
    { id: 'type', label: 'Typ', sortValue: (r) => r.type, render: typeChip },
    {
      id: 'webinar_datetime',
      label: 'Datum',
      tooltip: 'Sendetermin. Maßgeblich für den Zeitraum-Filter.',
      sortValue: (r) => r.webinar_datetime,
      render: (r) => `${formatGermanTime(r.webinar_datetime, 'D.M.YYYY HH:mm')} Uhr`,
    },
  ];

  const trailing: ExtraColumn<WebinarRevenuePerformanceData>[] = [
    {
      id: 'details',
      label: 'Details',
      render: (r) => (
        <Button
          size="small"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            onOpenWebinar(r.webinar_id);
          }}
          sx={{ fontSize: '0.75rem', padding: '2px 8px' }}
        >
          Details
        </Button>
      ),
    },
  ];

  // Summenzeile und Kacheln lesen dieselbe Summary. Teilnahme-Groessen darin ohne Warn-Webinare
  // (Backend), die Webinarzeilen roh — deshalb summieren sich diese Spalten bewusst nicht.
  const totals = s;
  const webinareOhneWarnung = s.webinar_count - excluded.length;
  const ohneWarnung = excluded.length
    ? `ohne ${excluded.length} Webinar(e) mit Tracking-Warnung`
    : undefined;

  const chartTheme = {
    axis: {
      domain: { line: { stroke: colors.grey[100] } },
      legend: { text: { fill: colors.grey[100] } },
      ticks: {
        line: { stroke: colors.grey[100], strokeWidth: 1 },
        text: { fill: colors.grey[100] },
      },
    },
    legends: { text: { fill: colors.grey[100] } },
  };

  return (
    <Box>
      <Explainer
        title="So lesen Sie diese Seite"
        sections={KPI_CONCEPTS.map((c) => ({ label: c.titel, body: c.text }))}
      />

      {excluded.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Tracking-Warnung bei {excluded.length} Webinar(en):</strong>{' '}
          {excluded.map((r) => r.webinar_title).join(', ')}. StealthSeminar hat dort kaum
          Teilnahme-Signale geliefert. Diese Webinare zählen bei Registrierungen, Anfos und Umsatz
          normal mit. Ihre rechtzeitig Angemeldeten, Teilnehmer und Anfos von Teilnehmern fehlen in
          Kacheln, Summenzeile und im Tab „Gruppierte Auswertungen“ — nur ihre eigene Zeile zeigt
          sie.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))',
          gap: '16px',
          mb: '24px',
        }}
      >
        <KpiTile
          metric="orders"
          value={s.orders}
          icon={<ShoppingCartTwoTone sx={{ fontSize: 36, color: colors.greenAccent[500] }} />}
          sub={`${n(s.orders_with_registration)} mit Anmeldung · ${n(s.attendee_orders)} von Teilnehmern`}
        />
        <KpiTile
          metric="cpa_revenue"
          value={s.cpa_revenue}
          icon={<EuroTwoTone sx={{ fontSize: 36, color: KPI.cpa_revenue.color }} />}
          sub={
            s.orders_without_cpa
              ? `${n(s.orders_without_cpa)} ${Number(s.orders_without_cpa) === 1 ? 'Anfo' : 'Anfos'} ohne CPA-Wert nicht enthalten`
              : undefined
          }
        />
        <KpiTile metric="ltv_revenue" value={s.ltv_revenue} />
        <KpiTile metric="paid_revenue" value={s.paid_revenue} />
        <KpiTile
          metric="registrations"
          value={s.registrations}
          icon={<PersonAddTwoTone sx={{ fontSize: 36, color: colors.greenAccent[500] }} />}
          sub={`${n(s.distinct_persons)} verschiedene Personen · ${n(s.webinar_count)} Webinare`}
        />
        <KpiTile
          metric="attendees"
          value={s.attendees}
          icon={<GroupsTwoTone sx={{ fontSize: 36, color: colors.greenAccent[500] }} />}
          sub={
            webinareOhneWarnung > 0
              ? `Ø ${n(Math.round(s.attendees / webinareOhneWarnung))} je Webinar${excluded.length ? ` · ${ohneWarnung}` : ''}`
              : undefined
          }
        />
        <KpiTile
          metric="show_up_rate"
          value={s.show_up_rate}
          icon={<TrendingUpTwoTone sx={{ fontSize: 36, color: colors.greenAccent[500] }} />}
          sub={ohneWarnung}
        />
        <KpiTile
          metric="attendee_conversion_rate"
          value={s.attendee_conversion_rate}
          sub={ohneWarnung}
        />
        <KpiTile metric="registration_conversion_rate" value={s.registration_conversion_rate} />
        <KpiTile metric="cpa_per_registration" value={s.cpa_per_registration} />
        <KpiTile metric="cpa_per_attendee" value={s.cpa_per_attendee} />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ color: colors.grey[100], mb: 0.5 }}>
          Webinare im Zeitraum
        </Typography>
        <Typography variant="body2" sx={{ color: colors.grey[300], mb: 1.5 }}>
          Klick auf eine Zeile öffnet das Webinar. Die Summenzeile zählt dieselben Personen und
          Anfos wie die Kacheln oben.
          {excluded.length > 0 &&
            ' Rechtzeitig, Teilnehmer und Anfos von Teilnehmern ergeben in der Summenzeile weniger als die Summe der Zeilen, weil Webinare mit Tracking-Warnung dort fehlen.'}
        </Typography>
        <MetricsTable
          rows={data.performance}
          leading={leading}
          metrics={TABLE_METRICS}
          trailing={trailing}
          totals={totals}
          totalsLabel={excluded.length ? 'Insgesamt (Teilnahme ohne Warn-Webinare)' : 'Insgesamt'}
          rowKey={(r) => r.webinar_id}
          defaultSortBy="webinar_datetime"
          onRowClick={(r) => onOpenWebinar(r.webinar_id)}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ color: colors.grey[100], mb: 2 }}>
          {KPI.cpa_revenue.label} nach Produkt
        </Typography>
        <Box
          sx={{
            height: '400px',
            backgroundColor: colors.primary[400],
            p: '20px',
            borderRadius: '8px',
          }}
        >
          <ResponsiveBar
            data={data.products.map((p) => ({
              product: p.product_name || p.product_code,
              umsatz: p.cpa_revenue,
            }))}
            keys={['umsatz']}
            indexBy="product"
            margin={{ top: 30, right: 40, bottom: 70, left: 90 }}
            padding={0.3}
            colorBy="indexValue"
            colors={{ scheme: 'set2' }}
            axisBottom={{ tickRotation: -30 }}
            axisLeft={{ legend: 'Umsatz CPA (€)', legendPosition: 'middle', legendOffset: -70 }}
            valueFormat={(v) => formatKpi('cpa_revenue', Number(v))}
            labelSkipWidth={40}
            labelSkipHeight={14}
            theme={chartTheme}
          />
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ color: colors.grey[100], mb: 2 }}>
          Vergleich nach Webinar-Typ
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(260px, 100%), 1fr))',
            gap: '16px',
          }}
        >
          {data.types.map((t) => (
            <Paper key={t.type} sx={{ p: 2, backgroundColor: colors.primary[400] }}>
              <Box sx={{ mb: 1.5 }}>{typeChip({ type: t.type })}</Box>
              {(
                [
                  ['Webinare mit Anfos', n(t.webinar_count)],
                  [KPI.orders.label, formatKpi('orders', t.orders)],
                  [KPI.cpa_revenue.label, formatKpi('cpa_revenue', t.cpa_revenue)],
                  [
                    KPI.avg_cpa_per_order.label,
                    formatKpi('avg_cpa_per_order', t.avg_cpa_per_order),
                  ],
                  [KPI.paid_revenue.label, formatKpi('paid_revenue', t.paid_revenue)],
                ] as [string, string][]
              ).map(([label, value]) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: colors.grey[200] }}>{label}</Typography>
                  <Typography sx={{ color: colors.greenAccent[500], fontWeight: 600 }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Paper>
          ))}
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ color: colors.grey[100], mb: 0.5 }}>
          {KPI.cpa_revenue.label} nach Webinar-Monat
        </Typography>
        <Typography variant="body2" sx={{ color: colors.grey[300], mb: 2 }}>
          Monat des Sendetermins, nicht des Kaufs.
        </Typography>
        <Box
          sx={{
            height: '380px',
            backgroundColor: colors.primary[400],
            p: '20px',
            borderRadius: '8px',
          }}
        >
          <ResponsiveLine
            data={[
              {
                id: KPI.cpa_revenue.label,
                data: data.timeline.map((m) => ({ x: m.month, y: m.cpa_revenue })),
              },
              {
                id: KPI.paid_revenue.label,
                data: data.timeline.map((m) => ({ x: m.month, y: m.paid_revenue })),
              },
            ]}
            margin={{ top: 30, right: 190, bottom: 50, left: 90 }}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: 0, max: 'auto' }}
            axisLeft={{ legend: '€', legendPosition: 'middle', legendOffset: -70 }}
            pointSize={9}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            useMesh
            legends={[
              {
                anchor: 'bottom-right',
                direction: 'column',
                translateX: 180,
                itemWidth: 170,
                itemHeight: 20,
                symbolSize: 12,
                symbolShape: 'circle',
              },
            ]}
            theme={chartTheme}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default RevenueAttributionView;
